import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { GeoJSONPolygon } from '../../types/orchard';

// =============================================================================
// TOUCH-FRIENDLY POLYGON DRAWING
// -----------------------------------------------------------------------------
// Renders a fixed crosshair at the map centre plus Add / Undo / Finish controls.
// The map itself is left fully interactive — pan, pinch-zoom, double-tap etc.
// all work normally, so the user can position the crosshair freely and only
// places a vertex when they deliberately tap "+ Add Point".
// =============================================================================

interface CrosshairPolygonDrawProps {
  color?: string;
  fillOpacity?: number;
  /** Initial vertices in Leaflet [lat, lng] order. Useful for redraw/edit flows. */
  initialVertices?: [number, number][];
  minVertices?: number;
  onComplete: (polygon: GeoJSONPolygon) => void;
  onCancel?: () => void;
}

const CrosshairPolygonDraw: React.FC<CrosshairPolygonDrawProps> = ({
  color = '#3b82f6',
  fillOpacity = 0.2,
  initialVertices,
  minVertices = 3,
  onComplete,
  onCancel,
}) => {
  const map = useMap();

  const [vertices, setVertices] = useState<L.LatLng[]>(() =>
    (initialVertices ?? []).map(([lat, lng]) => L.latLng(lat, lng))
  );

  // Mirror of vertices so layer/drag callbacks can read the freshest value
  // without needing to sit inside the render-driven effect.
  const verticesRef = useRef<L.LatLng[]>(vertices);
  verticesRef.current = vertices;

  // Stash the latest callback refs so the controls effect can run once without
  // needing onComplete / onCancel in its dependency list.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Refs for the buttons so we can toggle enabled state as vertex count changes
  const finishBtnRef = useRef<HTMLAnchorElement | null>(null);
  const undoBtnRef = useRef<HTMLAnchorElement | null>(null);

  // ---------------------------------------------------------------------------
  // Crosshair overlay — a DOM element appended to the map container
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const container = map.getContainer();
    const crosshair = document.createElement('div');
    crosshair.innerHTML = `
      <svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
        <g stroke="rgba(255,255,255,0.95)" stroke-width="3" fill="none" stroke-linecap="round">
          <line x1="22" y1="2" x2="22" y2="16"/>
          <line x1="22" y1="28" x2="22" y2="42"/>
          <line x1="2" y1="22" x2="16" y2="22"/>
          <line x1="28" y1="22" x2="42" y2="22"/>
        </g>
        <g stroke="#dc2626" stroke-width="1.5" fill="none" stroke-linecap="round">
          <line x1="22" y1="2" x2="22" y2="16"/>
          <line x1="22" y1="28" x2="22" y2="42"/>
          <line x1="2" y1="22" x2="16" y2="22"/>
          <line x1="28" y1="22" x2="42" y2="22"/>
        </g>
        <circle cx="22" cy="22" r="2.5" fill="#dc2626" stroke="white" stroke-width="1"/>
      </svg>
    `;
    crosshair.style.cssText =
      'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);' +
      'pointer-events:none;z-index:1000;filter:drop-shadow(0 0 2px rgba(0,0,0,0.6));';
    container.appendChild(crosshair);
    return () => {
      if (crosshair.parentNode === container) {
        container.removeChild(crosshair);
      }
    };
  }, [map]);

  // ---------------------------------------------------------------------------
  // Render markers / polyline / polygon fill whenever vertices change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const layers: L.Layer[] = [];

    // Polygon fill preview once we have 3+ points
    if (vertices.length >= 3) {
      const poly = L.polygon(vertices, {
        color,
        weight: 2,
        fillOpacity,
        interactive: false,
      }).addTo(map);
      layers.push(poly);
    }

    // Polyline connecting all placed vertices (visible from 2+ points)
    let polyline: L.Polyline | null = null;
    if (vertices.length >= 2) {
      polyline = L.polyline(vertices, {
        color,
        weight: 2,
        interactive: false,
      }).addTo(map);
      layers.push(polyline);
    }

    // Draggable vertex markers
    vertices.forEach((ll, idx) => {
      const icon = L.divIcon({
        className: 'crosshair-vertex-marker',
        html:
          `<div style="width:18px;height:18px;border-radius:50%;` +
          `background:${color};border:3px solid white;` +
          `box-shadow:0 0 4px rgba(0,0,0,0.6);box-sizing:border-box;"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      const marker = L.marker(ll, { icon, draggable: true }).addTo(map);
      marker.on('drag', (e: L.LeafletEvent) => {
        const newLl = (e.target as L.Marker).getLatLng();
        const next = verticesRef.current.slice();
        next[idx] = newLl;
        verticesRef.current = next;
        // Live-update preview layers without triggering a React re-render
        if (polyline) polyline.setLatLngs(next);
        const polyLayer = layers.find((l) => l instanceof L.Polygon) as L.Polygon | undefined;
        if (polyLayer) polyLayer.setLatLngs(next);
      });
      marker.on('dragend', () => {
        setVertices(verticesRef.current.slice());
      });
      layers.push(marker);
    });

    return () => {
      layers.forEach((l) => map.removeLayer(l));
    };
  }, [vertices, map, color, fillOpacity]);

  // ---------------------------------------------------------------------------
  // Control buttons — created once, state updated via refs
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const BTN_BASE =
      'display:flex;align-items:center;justify-content:center;font-size:11px;' +
      'font-weight:bold;padding:0 10px;height:34px;color:white;text-decoration:none;' +
      'cursor:pointer;white-space:nowrap;';

    const makeControl = (
      bg: string,
      html: string,
      title: string,
      onClick: () => void,
      btnRef?: React.MutableRefObject<HTMLAnchorElement | null>
    ) => {
      return L.Control.extend({
        onAdd() {
          const c = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
          const btn = L.DomUtil.create('a', '', c) as HTMLAnchorElement;
          btn.href = '#';
          btn.innerHTML = html;
          btn.title = title;
          btn.style.cssText = BTN_BASE + `background:${bg};`;
          if (btnRef) btnRef.current = btn;
          L.DomEvent.on(btn, 'click', (e) => {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);
            onClick();
          });
          L.DomEvent.disableClickPropagation(c);
          L.DomEvent.disableScrollPropagation(c);
          return c;
        },
        onRemove() {
          if (btnRef) btnRef.current = null;
        },
      });
    };

    const AddCtl = makeControl('#2563eb', '&#43; Add Point', 'Add a point at the crosshair', () => {
      const center = map.getCenter();
      setVertices((v) => [...v, center]);
    });

    const UndoCtl = makeControl(
      '#f59e0b',
      '&#8617; Undo',
      'Remove last point',
      () => {
        setVertices((v) => (v.length > 0 ? v.slice(0, -1) : v));
      },
      undoBtnRef
    );

    const FinishCtl = makeControl(
      '#16a34a',
      '&#10003; Finish',
      `Finish polygon (need ${minVertices}+ points)`,
      () => {
        const current = verticesRef.current;
        if (current.length < minVertices) return;
        const ring = current.map((ll) => [ll.lng, ll.lat] as [number, number]);
        // Close the ring per GeoJSON spec
        ring.push([current[0].lng, current[0].lat]);
        const polygon: GeoJSONPolygon = {
          type: 'Polygon',
          coordinates: [ring],
        };
        onCompleteRef.current(polygon);
      },
      finishBtnRef
    );

    const addInstance = new (AddCtl as any)({ position: 'topright' });
    const undoInstance = new (UndoCtl as any)({ position: 'topright' });
    const finishInstance = new (FinishCtl as any)({ position: 'topright' });

    addInstance.addTo(map);
    undoInstance.addTo(map);
    finishInstance.addTo(map);

    let cancelInstance: L.Control | null = null;
    if (onCancelRef.current) {
      const CancelCtl = makeControl('#6b7280', '&#10007; Cancel', 'Cancel drawing', () => {
        onCancelRef.current?.();
      });
      cancelInstance = new (CancelCtl as any)({ position: 'topright' });
      cancelInstance!.addTo(map);
    }

    return () => {
      addInstance.remove();
      undoInstance.remove();
      finishInstance.remove();
      if (cancelInstance) cancelInstance.remove();
    };
  }, [map, minVertices]);

  // ---------------------------------------------------------------------------
  // Sync enabled state of Undo/Finish buttons with vertex count
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const finishBtn = finishBtnRef.current;
    if (finishBtn) {
      const enabled = vertices.length >= minVertices;
      finishBtn.style.opacity = enabled ? '1' : '0.5';
      finishBtn.style.pointerEvents = enabled ? 'auto' : 'none';
    }
    const undoBtn = undoBtnRef.current;
    if (undoBtn) {
      const enabled = vertices.length > 0;
      undoBtn.style.opacity = enabled ? '1' : '0.5';
      undoBtn.style.pointerEvents = enabled ? 'auto' : 'none';
    }
  }, [vertices, minVertices]);

  return null;
};

export default CrosshairPolygonDraw;
